import { useMemo, useState } from "react";
import Avatar from "../components/Avatar";
import { FragenWahl, ModusWahl, ZeitWahl } from "../components/Auswahl";
import Button from "../components/Button";
import Icon from "../components/icons/Icon";
import Notiz from "../components/Notiz";
import SpielMenue from "../components/SpielMenue";
import StatusChip from "../components/StatusChip";
import TextFeld from "../components/TextFeld";
import { farbNummern } from "../spieler";
import "./SetupScreen.css";

/**
 * Spiel einrichten (Host, nach dem Spotify-Login): Modus, Spieler,
 * Fragen, Zeit. laufenderRaum: Code eines noch offenen eigenen Raums —
 * dann fuehrt der Hauptknopf dorthin zurueck statt in einen neuen.
 * children: Dialoge, die ueber dem Setup aufgehen koennen.
 */
export default function SetupScreen({
  spotifyStatus,
  onOpenDevices,
  gameMode,
  setGameMode,
  players,
  setPlayers,
  onAddPlayer,
  hostName = "",
  setHostName,
  activeTasks,
  setActiveTasks,
  timeLimit,
  setTimeLimit,
  onStart,
  laufenderRaum,
  onZurueckInLobby,
  onLobbyOeffnen,
  onMitspielen,
  onLogout,
  children,
}) {
  const [menue, setMenue] = useState(false);
  const name = (p, i) => p.name.trim() || `Spieler ${i + 1}`;
  const farbe = useMemo(() => farbNummern(players.map(name)), [players]);
  const keineFrage = !Object.values(activeTasks).some(Boolean);

  let haupt;
  if (gameMode === "CLASSIC") {
    haupt = (
      <Button variant="haupt" disabled={keineFrage} onClick={onStart}>
        <Icon name="play" />
        Spiel starten
      </Button>
    );
  } else if (laufenderRaum) {
    // Laeuft noch ein eigener Raum, fuehrt der Weg dorthin zurueck. Ein neuer
    // Raum bekommt einen neuen Code — die Mitspieler sassen danach in einem
    // Raum ohne Host und warteten ewig.
    haupt = (
      <>
        <Button variant="haupt" onClick={onZurueckInLobby}>
          Zurück in die Lobby {laufenderRaum}
          <Icon name="pfeil" />
        </Button>
        <Button variant="text" onClick={onLobbyOeffnen}>
          Neuen Raum öffnen (neuer Code)
        </Button>
      </>
    );
  } else {
    haupt = (
      <Button variant="haupt" disabled={keineFrage || !hostName.trim()} onClick={onLobbyOeffnen}>
        Lobby öffnen
        <Icon name="pfeil" />
      </Button>
    );
  }

  return (
    <div className="spiel setup">
      <header className="spiel-kopf">
        <div className="spiel-kopf-runde">
          <small>Neues Spiel</small>
          <strong>Einrichten</strong>
        </div>
        <StatusChip art="spotify" status={spotifyStatus} onClick={onOpenDevices} />
        <button type="button" className="ikon-knopf" onClick={() => setMenue(true)} aria-label="Menü" title="Menü">
          <Icon name="menue" />
        </button>
      </header>

      <main className="spiel-mitte oben">
        <ol className="schritte" aria-label="So geht's">
          <li>
            <b>1</b>Spotify öffnen
          </li>
          <li>
            <b>2</b>Playlist starten
          </li>
          <li>
            <b>3</b>Hier loslegen
          </li>
        </ol>

        <section className="abschnitt">
          <h2>Spielmodus</h2>
          <ModusWahl wert={gameMode} onChange={setGameMode} />
        </section>

        {gameMode === "BUZZER" ? (
          <>
            {/* Der Host spielt im Buzzer-Modus immer mit — er tritt mit
                diesem Namen automatisch seinem Raum bei. */}
            <section className="abschnitt">
              <h2>Dein Name</h2>
              <div className="eingabe-zeile">
                <Avatar name={hostName.trim() || "?"} farbe={1} />
                <TextFeld
                  className="klein"
                  value={hostName}
                  placeholder="Dein Name"
                  aria-label="Dein Name"
                  maxLength={20}
                  onChange={(e) => setHostName(e.target.value.slice(0, 20))}
                />
              </div>
            </section>
            <Notiz icon="personen">
              <b>Die anderen tragen sich selbst ein.</b> Du bekommst gleich einen Code samt QR – jeder spielt am
              eigenen Handy, du auch.
            </Notiz>
          </>
        ) : (
          <section className="abschnitt">
            <div className="abschnitt-titel">
              <h2>Wer spielt?</h2>
              <span>{players.length} Leute</span>
            </div>
            {players.map((p, i) => (
              <div key={i} className="eingabe-zeile">
                <Avatar name={name(p, i)} farbe={farbe(name(p, i))} />
                <TextFeld
                  className="klein"
                  value={p.name}
                  placeholder={`Name Spieler ${i + 1}`}
                  aria-label={`Name Spieler ${i + 1}`}
                  onChange={(e) =>
                    setPlayers((prev) => prev.map((pl, idx) => (idx === i ? { ...pl, name: e.target.value.slice(0, 20) } : pl)))
                  }
                />
                {players.length > 2 && (
                  <button
                    type="button"
                    className="eingabe-weg"
                    onClick={() => setPlayers(players.filter((_, idx) => idx !== i))}
                    aria-label={`${name(p, i)} entfernen`}
                    title={`${name(p, i)} entfernen`}
                  >
                    <Icon name="x" />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="neu-zeile" onClick={onAddPlayer}>
              <Icon name="plus" />
              Spieler hinzufügen
            </button>
          </section>
        )}

        <section className="abschnitt">
          <h2>Was wird gefragt?</h2>
          <FragenWahl activeTasks={activeTasks} setActiveTasks={setActiveTasks} />
          {keineFrage && <p className="hinweis-text setup-hinweis">Mindestens eine Frage auswählen.</p>}
        </section>

        <section className="abschnitt">
          <h2>Zeit pro Song</h2>
          <ZeitWahl wert={timeLimit} onChange={setTimeLimit} />
        </section>

        {/* Auch nach dem Spotify-Login erreichbar: vorher gab es diesen
            Einstieg NUR auf dem Start, und dorthin kommt man nach der
            Anmeldung nicht mehr zurueck. */}
        <div className="trenner">oder</div>
        <Button variant="text" onClick={onMitspielen}>
          <Icon name="handy" />
          Bei jemand anderem mitspielen
        </Button>
      </main>

      <div className="daumenzone klebt">{haupt}</div>

      {menue && (
        <SpielMenue
          onClose={() => setMenue(false)}
          eintraege={[
            {
              icon: "lautsprecher",
              label: "Spotify-Gerät wählen",
              onClick: () => {
                setMenue(false);
                onOpenDevices();
              },
            },
            onLogout && {
              icon: "tuer",
              label: "Abmelden",
              gefahr: true,
              frage: "Du meldest dich von Spotify ab. Punkte und Verlauf werden gelöscht.",
              bestaetigen: "Abmelden",
              onClick: onLogout,
            },
          ]}
        />
      )}
      {children}
    </div>
  );
}
